import React from 'react';
import {useTranslation} from "react-i18next";

const ModalPlayerDetail = ({player, onClose}) => {
    const {t} = useTranslation();

    if (!player) return null;

    const isRugby = player.type === 'RUGBY';

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
            <div
                className="absolute inset-0 bg-black/60 backdrop-blur-[2px] transition-opacity"
                onClick={onClose}
            ></div>

            <div
                className="bg-white rounded-[24px] p-6 w-full max-w-[360px] relative z-10 shadow-2xl animate-pop-in-flex">

                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
                >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                              d="M6 18L18 6M6 6l12 12"></path>
                    </svg>
                </button>

                <div className="mb-6">
                    <div className="text-xs text-gray-400 font-bold uppercase mb-1">{t('modal_player.title')}</div>
                    <h3 className="text-2xl font-black text-brand-dark leading-none mb-2">{player.pseudo}</h3>
                </div>

                {isRugby ? (
                    <RugbyContent player={player} t={t}/>
                ) : (
                    <FootballContent player={player} t={t}/>
                )}

                {player.quizBonus > 0 && (
                    <div className="space-y-1.5 pt-3 mt-1 border-t border-gray-100">
                        <div className="flex justify-between items-center">
                            <span className="text-xs font-bold text-gray-400 uppercase">{t('modal_player.prono', 'Prono')}</span>
                            <span className="text-sm font-bold text-gray-600">{Math.round(player.pronoPoints).toLocaleString()} {t('common.points')}</span>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-xs font-bold text-action-green uppercase flex items-center gap-1">
                                <span>⚡</span> {t('modal_player.quiz_bonus', 'Bonus quiz')}
                            </span>
                            <span className="text-sm font-black text-action-green">+{player.quizBonus.toLocaleString()} {t('common.points')}</span>
                        </div>
                    </div>
                )}

                <div className={`flex justify-between items-center pt-4 mt-2 ${player.quizBonus > 0 ? '' : 'border-t border-gray-100'}`}>
                    <span className="text-xs font-bold text-gray-400 uppercase">{t('modal_player.total')}</span>
                    <span className="text-3xl font-black text-brand-dark tracking-tighter">{Math.round(player.points).toLocaleString()} <span
                        className="text-sm font-bold text-gray-400">{t('common.points')}</span></span>
                </div>

                {/* Bouton Fermer Principal */}
                <button
                    onClick={onClose}
                    className="w-full mt-6 py-3 bg-brand-dark text-white font-bold rounded-xl shadow-lg hover:brightness-110 active:scale-98 transition-transform"
                >
                    {t('modal_player.close_btn')}
                </button>
            </div>
        </div>
    );
};

export default ModalPlayerDetail;

const FootballContent = ({player, t}) => (
    <>
        <div
            className="bg-gray-50 rounded-2xl p-4 text-center mb-6 border border-gray-100 flex justify-center items-center gap-4">
            <div className="text-5xl font-black text-score-neon tracking-tighter">
                {player.betDisplay}
            </div>
            <div className="text-left">
                <div
                    className="text-[10px] text-gray-400 font-bold uppercase tracking-wide">{t('modal_player.stake')}</div>
                <div className="text-lg font-bold text-gray-800">{t('modal_player.stake_value')}</div>
            </div>
        </div>
        <div className="space-y-2 mb-4">
            <div
                className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-lg p-2 border-l-4 border-l-win-blue">
                <div className="flex items-center gap-2">
                    <span className="text-xl">🏆</span>
                    <span className="text-xs font-bold text-slate-500 uppercase">{t('modal_player.winner')}</span>
                </div>
                <div className="flex items-center gap-2">
                    <span className="bg-blue-100 text-blue-800 text-[10px] font-black px-1.5 py-0.5 rounded">
                        x{Number(player.oddsWin || 1.5).toFixed(2)}
                    </span>
                    <span className="font-black text-win-blue">
                        +{player.oddsWin ? Math.round(player.oddsWin * 100) /* Simul point */ : 0}
                    </span>
                </div>
            </div>
            <div
                className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-lg p-2 border-l-4 border-l-exact-gold">
                <div className="flex items-center gap-2">
                    <span className="text-xl">🎯</span>
                    <span className="text-xs font-bold text-slate-500 uppercase">{t('modal_player.gain_exact')}</span>
                </div>
                <div className="flex items-center gap-2">
                    <span className="bg-amber-100 text-amber-800 text-[10px] font-black px-1.5 py-0.5 rounded">
                        x{Number(player.oddsScore || 3).toFixed(2)}
                    </span>
                    <span className="font-black text-exact-gold">
                        +{player.oddsScore ? Math.round(player.oddsScore * 100) : 0}
                    </span>
                </div>
            </div>
        </div>
    </>
);

const RugbyContent = ({player, t}) => {
    const isDraw = !player.winningTeamName;
    const logoUrl = player.winningTeamLogoUrl;
    const teamName = player.winningTeamName || t('modal_player.rugby.unknown_team');
    const margin = player.predictionMargin || 0;
    const odds = player.odds || 1.0;

    const stats = player.stats || {isCorrectWinner: false, realMargin: 0, marginDiff: 0, totalPenalty: 0};
    const isWinning = stats.isCorrectWinner !== false;
    const hasPenalty = stats.totalPenalty > 0;

    const maxPoints = player.maxPotentialPoints;
    const currentBasePoints = Math.max(0, maxPoints - stats.totalPenalty);

    return (
        <>
            <div
                className="bg-slate-50 border border-slate-200 rounded-2xl p-4 mb-4 flex items-center justify-between relative shadow-sm">
                <div className="flex items-center gap-4 z-10">
                    <div
                        className="w-14 h-14 bg-white border border-slate-100 p-2 rounded-xl flex items-center justify-center shadow-sm">
                        {isDraw ? (
                            <span className="text-2xl">🤝</span>
                        ) : (
                            logoUrl ? (
                                <img src={logoUrl} alt={teamName} className="w-full h-full object-contain"/>
                            ) : (
                                <span className="text-slate-300 font-bold text-xl">?</span>
                            )
                        )}
                    </div>
                    <div className="flex flex-col">
                        <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wide">
                            {t('modal_player.rugby.valid_prono')}
                        </span>
                        <div className="font-black text-lg text-slate-800 leading-tight">
                            {isDraw ? t('modal_player.rugby.draw') : teamName}
                        </div>
                        <div className="mt-1">
                            <span
                                className="inline-flex items-center gap-1 bg-white border border-slate-200 px-2 py-0.5 rounded-md text-[10px] font-bold text-slate-500 shadow-sm">
                                <span>{t('modal_player.rugby.odds')}</span>
                                <span className="text-slate-900">x{Number(odds).toFixed(2)}</span>
                            </span>
                        </div>
                    </div>
                </div>
                {!isDraw && (
                    <div className="flex flex-col items-end z-10 pl-4 border-l border-slate-200/60">
                        <span
                            className="text-[9px] text-slate-400 uppercase font-bold mb-0.5">{t('modal_player.rugby.margin')}</span>
                        <span
                            className="text-4xl font-black text-slate-800 leading-none tracking-tighter">+{margin}</span>
                    </div>
                )}
            </div>

            <div className="mb-6">
                {!isWinning ? (
                    <div className="bg-red-50 border border-red-100 rounded-xl p-4 flex items-center gap-4">
                        <div
                            className="w-10 h-10 bg-white text-red-500 rounded-full flex items-center justify-center text-xl shadow-sm border border-red-100">
                            ❌
                        </div>
                        <div>
                            <div
                                className="font-black text-red-900 uppercase text-sm">{t('modal_player.rugby.incorrect_winner')}</div>
                            <div
                                className="text-xs text-red-700 font-medium">{t('modal_player.rugby.losing_moment')}</div>
                        </div>
                    </div>
                ) : (
                    <>
                        <div
                            className="flex w-full bg-white border border-gray-200 rounded-t-xl overflow-hidden shadow-sm relative z-10">
                            <div className="flex-1 flex items-center justify-around bg-slate-50 p-2 relative">
                                <div className="flex flex-col items-center">
                                    <span
                                        className="text-[9px] text-gray-400 font-bold uppercase tracking-wide">{t('modal_player.rugby.reality')}</span>
                                    <span className="text-xl font-black text-slate-700">+{stats.realMargin}</span>
                                </div>
                                <div className="w-px h-8 bg-gray-200"></div>
                                <div className="flex flex-col items-center">
                                    <span
                                        className="text-[9px] text-gray-400 font-bold uppercase tracking-wide">{t('modal_player.rugby.margin')}</span>
                                    <span
                                        className={`text-xl font-black ${stats.marginDiff === 0 ? 'text-emerald-500' : 'text-slate-700'}`}>{stats.marginDiff}</span>
                                </div>
                            </div>
                            <div
                                className={`w-[40%] flex flex-col items-center justify-center p-2 relative overflow-hidden ${hasPenalty ? 'bg-red-50' : 'bg-emerald-50'}`}>
                                <div
                                    className={`absolute -right-4 -top-4 w-12 h-12 rounded-full blur-xl opacity-40 ${hasPenalty ? 'bg-red-500' : 'bg-emerald-500'}`}></div>
                                <span
                                    className={`text-[9px] font-black uppercase tracking-widest z-10 ${hasPenalty ? 'text-red-700' : 'text-emerald-700'}`}>
                                    {hasPenalty ? t('modal_player.rugby.penalty') : t('modal_player.rugby.perfect')}
                                </span>
                                <div
                                    className={`text-2xl font-black leading-none z-10 ${hasPenalty ? 'text-red-600' : 'text-emerald-600'}`}>
                                    {hasPenalty ? `-${stats.totalPenalty}` : '0'}
                                </div>
                            </div>
                        </div>

                        <div
                            className="bg-slate-100 border-x border-b border-gray-200 rounded-b-xl p-3 flex items-center justify-between">
                            <div className="flex flex-col">
                                <span
                                    className="text-[9px] text-gray-400 font-bold uppercase tracking-wide">{t('modal_player.rugby.points_calc')}</span>
                                <div className="flex items-center gap-1.5 text-xs font-bold text-slate-500 mt-0.5">
                                    <span>{maxPoints}</span>
                                    <span>-</span>
                                    <span className={hasPenalty ? "text-red-500" : "text-emerald-600"}>
                                        {stats.totalPenalty}
                                    </span>
                                </div>
                            </div>
                            <div className="text-right">
                                <div className="text-xl font-black text-slate-800 leading-none">
                                    {currentBasePoints}
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </>
    );
};